import { useState } from "react";
import {
    Avatar, Box, Flex, Icon, IconButton, Stack, Text, Tooltip, useColorModeValue,
} from "@chakra-ui/react";
import { MdEdit, MdDeleteOutline, MdClose, MdEmail } from "react-icons/md";
import EditGroupDialog from "../dialogs/EditGroupDialog";
import DeleteGroupDialog from "../dialogs/DeleteGroupDialog";
import SendEmailDialog from "../../adminEmail/SendEmailDialog";

/**
 * Resolves the leader display label for the header.
 * @param {Object} group
 * @returns {string}
 */
function resolveLeaderLabel(group) {
    const leader = (group?.members || []).find((m) => m?.is_leader);
    if (!leader) return "Aucun leader";
    return `Leader : ${leader.surname || leader.email || `#${leader.id}`}`;
}

/**
 * Header of the group detail drawer (avatar, name, action buttons, close).
 * @param {Object} props
 * @param {Object} props.group
 * @param {Function} props.onClose
 * @param {Function} props.onChanged
 * @param {Function} props.onDeleted
 * @param {Function} props.onRefresh
 */
function GroupDetailHeader({ group, onClose, onChanged, onDeleted, onRefresh }) {
    const [editOpen, setEditOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [emailOpen, setEmailOpen] = useState(false);
    const nameColor = useColorModeValue("secondaryGray.900", "white");
    const subColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");

    return (
        <Flex align="center" gap="12px" pb="16px" mb="8px" borderBottom="1px solid" borderColor="whiteAlpha.200">
            <Avatar size="md" src={group?.photo || undefined} name={group?.name || "?"} />
            <Box flex="1" minW="0">
                <Text noOfLines={1} color={nameColor} fontSize="1rem" fontWeight="600">
                    {group?.name || "Sans nom"} · #{group?.id}
                </Text>
                <Text color={subColor} fontSize="0.75rem">{resolveLeaderLabel(group)}</Text>
            </Box>
            <Stack direction="row" spacing="4px">
                <Tooltip label="Envoyer un email">
                    <IconButton aria-label="Envoyer un email" variant="ghost" size="sm" color="brand.500"
                        icon={<Icon as={MdEmail} />} onClick={() => setEmailOpen(true)} />
                </Tooltip>
                <Tooltip label="Modifier">
                    <IconButton aria-label="Modifier" variant="ghost" size="sm" color="brand.500"
                        icon={<Icon as={MdEdit} />} onClick={() => setEditOpen(true)} />
                </Tooltip>
                <Tooltip label="Supprimer">
                    <IconButton aria-label="Supprimer" variant="ghost" size="sm" color="red.400"
                        icon={<Icon as={MdDeleteOutline} />} onClick={() => setDeleteOpen(true)} />
                </Tooltip>
                <Tooltip label="Fermer">
                    <IconButton aria-label="Fermer" variant="ghost" size="sm"
                        icon={<Icon as={MdClose} />} onClick={onClose} />
                </Tooltip>
            </Stack>
            <EditGroupDialog
                open={editOpen}
                group={group}
                onClose={() => setEditOpen(false)}
                onUpdated={(updated) => { setEditOpen(false); onRefresh?.(); onChanged?.(updated); }}
            />
            <DeleteGroupDialog
                open={deleteOpen}
                group={group}
                onClose={() => setDeleteOpen(false)}
                onDeleted={() => { setDeleteOpen(false); onDeleted?.(group?.id); }}
            />
            <SendEmailDialog open={emailOpen} onClose={() => setEmailOpen(false)} targetType="group"
                targetId={group?.id} recipientLabel={group?.name} />
        </Flex>
    );
}

export default GroupDetailHeader;
